import { useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearch, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Download,
  Folder as FolderIcon,
  FolderPlus,
  FolderInput,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { AssetKind } from '@shared/types';
import { AssetGrid } from '@/components/AssetGrid';
import { Button, CenteredSpinner, EmptyState, ErrorState } from '@/components/ui';
import { cn } from '@/lib/format';
import { isAssetDrag } from '@/lib/drag';
import { useSelection } from '@/lib/selection';
import { useToast } from '@/components/Toasts';
import { MoveDialog } from '@/components/MoveDialog';
import { keepBrowseFilters } from '@/lib/browse';
import { useAssets, useLibraryStats, useCreateFolder, useDeleteAssets, useFolders, useMoveAssets, useRestore, useUpload } from '@/lib/queries';
import { t } from '@/lib/i18n';
import { downloadFolderZip, uploadFiles } from '@/lib/zip-transfer';

// 'pdf' sits beside 'document' rather than inside it: PDFs are the artwork
// masters people look for by name, and Pimcore scatters them across two types.
const KINDS: (AssetKind | 'pdf' | 'font' | 'all')[] = ['all', 'image', 'pdf', 'document', 'video', 'audio', 'archive', 'font'];

// Display labels for the type buttons; resolved at call time so t() uses the
// active language.
const kindLabels = () => ({
  all: t('browsePage.all'),
  image: t('browsePage.images'),
  pdf: 'PDF',
  document: t('browsePage.documents'),
  video: t('browsePage.videos'),
  audio: t('browsePage.audio'),
  archive: t('browsePage.archives'),
  font: t('browsePage.fonts'),
});

export interface BrowseSearch {
  kind?: AssetKind | 'pdf' | 'font';
  sort?: string;
  order?: 'asc' | 'desc';
  untagged?: boolean;
  favourites?: boolean;
}

/** The root "All Assets" view, the smart views, and the per-folder view share this. */
export function BrowsePage() {
  const selection = useSelection();
  const toast = useToast();
  const moveMany = useMoveAssets();
  const [movePicker, setMovePicker] = useState(false);
  // folderId comes from the route param on /folders/$folderId; root has none.
  const params = useParams({ strict: false }) as { folderId?: number };
  const folderId = params.folderId;
  const search = useSearch({ strict: false }) as BrowseSearch;
  const navigate = useNavigate();

  const kind = search.kind ?? 'all';
  const sort = search.sort ?? 'name';

  const { data: folders } = useFolders();
  const folder = folderId ? folders?.items.find((f) => f.id === folderId) : undefined;

  /**
   * Direct subfolders of wherever we are, as pills — so drilling down doesn't
   * mean hunting for the node in the rail tree. The flat list is already
   * permission-scoped by /folders, so a folder the user cannot list simply
   * isn't here.
   *
   * At the root, `parentId` is Pimcore's root id (1) rather than null, so match
   * on "no parent among the folders we can see" instead of a literal null.
   */
  const subfolders = useMemo(() => {
    const all = folders?.items ?? [];
    if (!all.length) return [];
    const visible = new Set(all.map((f) => Number(f.id)));
    const children = folderId
      ? all.filter((f) => Number(f.parentId) === Number(folderId))
      : all.filter((f) => f.parentId === null || !visible.has(Number(f.parentId)));
    return [...children].sort((a, b) => a.name.localeCompare(b.name));
  }, [folders, folderId]);

  /**
   * Where "Back" goes: the parent folder, or the root listing when the parent
   * is above what this user can see.
   */
  const parentFolder = folder
    ? folders?.items.find((f) => Number(f.id) === Number(folder.parentId))
    : undefined;

  const query = useAssets({
    folderId,
    recursive: folderId === undefined ? true : true,
    kind: search.kind,
    untagged: search.untagged,
    favourites: search.favourites,
    sort,
    order: search.order,
    perPage: 200,
  });

  // Per-type counts for the type buttons, scoped to this folder.
  const { data: stats } = useLibraryStats(folderId);

  const upload = useUpload(folderId ?? null);
  const createFolder = useCreateFolder();
  const deleteAssets = useDeleteAssets();
  const restore = useRestore();
  const fileInput = useRef<HTMLInputElement>(null);

  // Update a single search param, preserving the folder route if any.
  function setParam(patch: Partial<BrowseSearch>) {
    void navigate({ to: '.', search: (prev: BrowseSearch) => ({ ...prev, ...patch }) as never });
  }

  const title = folder
    ? folder.name
    : search.favourites ? t('browsePage.favourites')
    : search.untagged ? t('browsePage.untagged')
    : search.kind === 'pdf' ? t('browsePage.pdfs')
    : search.kind ? ((kindLabels() as Record<string, string>)[search.kind] ?? `${search.kind[0]!.toUpperCase()}${search.kind.slice(1)}s`)
    : search.sort === 'created' ? t('browsePage.recent')
    : t('browsePage.allAssets');

  const subtitle = folder?.path ?? t('browsePage.entireLibrary');

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    const picked = Array.from(files);
    if (fileInput.current) fileInput.current.value = '';
    await uploadFiles(picked, folderId ?? null, (f) => upload.mutateAsync(f), (message) => toast.push({ message, tone: 'error' }));
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b line surface-card px-5 py-3">
        <div className="mr-auto min-w-0">
          <h1 className="truncate text-base font-semibold text-strong">{title}</h1>
          <p className="text-xs text-muted">
            {subtitle}
            {query.data ? ` · ${t('browsePage.nAssets', { n: query.data.total })}` : ''}
          </p>
        </div>

        <div className="flex rounded-lg surface-sunken p-0.5">
          {KINDS.map((k) => (
            <button
              key={k}
              onClick={() => setParam({ kind: k === 'all' ? undefined : k })}
              className={cn('rounded-md px-2.5 py-1 text-xs font-medium capitalize transition-colors', kind === k ? 'surface-card text-strong shadow-sm' : 'text-muted hover:text-strong')}
            >
              {(kindLabels() as Record<string, string>)[k] ?? k}
              {stats && (
                <span className="ml-1 text-[10px] font-normal tabular-nums text-faint">
                  {(k === 'all' ? stats.total : stats.byKind[k] ?? 0).toLocaleString()}
                </span>
              )}
            </button>
          ))}
        </div>

        <select value={sort} onChange={(e) => setParam({ sort: e.target.value })} className="rounded border line surface-card px-1.5 py-1 text-xs text-strong">
          <option value="name">{t('browsePage.sortName')}</option>
          <option value="created">{t('browsePage.sortNewest')}</option>
          <option value="size">{t('browsePage.sortSize')}</option>
          <option value="kind">{t('browsePage.sortType')}</option>
        </select>

        {folder && (
          <Button
            variant="secondary"
            className="h-8"
            title={t('zip.downloadFolderHint')}
            onClick={() => void downloadFolderZip(folder.id, folder.name, (message) => toast.push({ message, tone: 'error' }))}
          >
            <Download className="size-3.5" /> {t('zip.downloadFolder')}
          </Button>
        )}
        <Button variant="secondary" className="h-8" onClick={() => createFolder.mutate({ name: t('browsePage.newFolder'), parentId: folderId ?? null })}>
          <FolderPlus className="size-3.5" /> {t('browsePage.folder')}
        </Button>
        <Button variant="primary" className="h-8" loading={upload.isPending} onClick={() => fileInput.current?.click()}>
          <Upload className="size-3.5" /> {t('browsePage.upload')}
        </Button>
        <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => void handleFiles(e.target.files)} />
      </header>

      {/* Drill-down row: Back plus this folder's direct subfolders as pills, so
          filtering down doesn't require finding the node in the rail tree.
          Hidden entirely at the root with no subfolders, so pages that have
          nowhere to go don't grow an empty bar. */}
      {(folderId !== undefined || subfolders.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 border-b line surface-card px-5 py-2">
          {folderId !== undefined && (
            <Link
              // '/all' is the root asset listing; '/' is the dashboard, which
              // is not where "up one level" should land.
              to={parentFolder ? '/folders/$folderId' : '/all'}
              {...(parentFolder ? { params: { folderId: parentFolder.id } } : {})}
              search={keepBrowseFilters as never}
              className="mr-1 inline-flex items-center gap-1 rounded-full border line px-2 py-0.5 text-[11px] text-muted transition-colors hover:border-brand-500 hover:text-strong"
            >
              <ArrowLeft className="size-3" />
              {parentFolder ? parentFolder.name : t('browsePage.allAssetsLower')}
            </Link>
          )}
          {subfolders.map((f) => (
            <Link
              key={f.id}
              to="/folders/$folderId"
              params={{ folderId: f.id }}
              search={keepBrowseFilters as never}
              title={f.path}
              className="inline-flex max-w-[220px] items-center gap-1 rounded-full border line surface-card px-2 py-0.5 text-[11px] text-body transition-colors hover:border-brand-500 hover:text-strong"
            >
              <FolderIcon className="size-3 shrink-0 text-faint" />
              <span className="truncate">{f.name}</span>
              {typeof f.assetCount === 'number' && f.assetCount > 0 && (
                <span className="shrink-0 tabular-nums text-faint">{f.assetCount.toLocaleString()}</span>
              )}
            </Link>
          ))}
        </div>
      )}

      {/* Bulk action bar — only present once something is selected, so the
          normal browsing view is unchanged. */}
      {selection.active && (
        <div className="flex items-center gap-2 border-b line bg-brand-50 px-4 py-2 text-[13px] dark:bg-brand-900/30">
          <span className="font-medium text-strong">{t('browsePage.nSelected', { n: selection.ids.size })}</span>
          <button
            onClick={() => setMovePicker(true)}
            className="ml-2 flex items-center gap-1.5 rounded-lg bg-brand-600 px-2.5 py-1 font-medium text-white hover:bg-brand-700"
          >
            <FolderInput className="size-3.5" /> {t('browsePage.moveTo')}
          </button>
          {/* Deliberately NOT gated on isAdmin. Pimcore's `delete` grant is
              per-folder and independent of the admin flag — dev's own
              `test_deleter` holds all nine rights on /INTERNAL while
              isAdmin=false, and an admin-only button would hide the action from
              exactly the people entitled to it. The server checks the grant per
              asset, so a user who holds it on some folders and not others gets
              a partial result; that is why the toast reports counts. */}
          {(
            <button
              onClick={() => {
                const ids = [...selection.ids];
                const label = ids.length === 1 ? t('browsePage.oneItem') : t('browsePage.nItems', { n: ids.length });
                if (!window.confirm(t('browsePage.deleteConfirm', { label }))) return;
                deleteAssets.mutate(ids, {
                  onSuccess: (r) => {
                    selection.clear();
                    toast.push({
                      message: r.failed.length
                        ? t('browsePage.deletedWithFailed', { deleted: r.deleted, failed: r.failed.length })
                        : t('browsePage.deletedLabel', { label }),
                      tone: r.failed.length ? 'error' : 'default',
                      // Undo only what was actually binned. No handles means
                      // nothing is restorable, so no button.
                      onUndo: r.handles.length
                        ? async () => {
                            await restore.mutateAsync(r.handles);
                          }
                        : undefined,
                    });
                  },
                });
              }}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-medium text-red-700 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-950/50"
            >
              <Trash2 className="size-3.5" /> {t('browsePage.delete')}
            </button>
          )}
          <button
            onClick={() => selection.clear()}
            className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 text-muted hover:bg-black/5 dark:hover:bg-white/10"
          >
            <X className="size-3.5" /> {t('browsePage.clear')}
          </button>
        </div>
      )}

      {movePicker && (
        <MoveDialog
          count={selection.ids.size}
          busy={moveMany.isPending}
          onCancel={() => setMovePicker(false)}
          onConfirm={(destinationId) => {
            const ids = [...selection.ids];
            // The folder currently being viewed — captured before the move so
            // undo can put them back. Undefined at the root or in a smart view,
            // where the items may have come from many folders and there is no
            // single place to restore to.
            //
            // This reads the PAGE's folderId. The dialog's callback parameter
            // used to be named `folderId` too, which shadowed it, so `from` was
            // the destination and Undo moved everything to where it already was
            // — a silent no-op that looked like it had worked.
            const from = folderId ?? null;
            moveMany.mutate(
              { ids, folderId: destinationId },
              {
                onSuccess: (r) => {
                  setMovePicker(false);
                  selection.clear();
                  const failed = r.failed.length;
                  toast.push({
                    message: failed
                      ? t('browsePage.movedWithFailed', { moved: r.moved, failed })
                      : r.moved === 1
                        ? t('browsePage.movedOne', { n: r.moved })
                        : t('browsePage.movedN', { n: r.moved }),
                    tone: failed ? 'error' : 'default',
                    onUndo:
                      from === null
                        ? undefined
                        : async () => {
                            await moveMany.mutateAsync({ ids, folderId: from });
                          },
                  });
                },
              },
            );
          }}
        />
      )}

      <div
        className="scroll-thin min-h-0 flex-1 overflow-y-auto"
        // Files dragged from the desktop upload here. An asset dragged from the
        // grid is a MOVE, targeted at a folder in the rail, so it is ignored:
        // without this, dragging a tile and missing the rail would re-upload it.
        onDragOver={(e) => { if (!isAssetDrag(e)) e.preventDefault(); }}
        onDrop={(e) => {
          if (isAssetDrag(e)) return;
          e.preventDefault();
          void handleFiles(e.dataTransfer.files);
        }}
      >
        {query.isPending ? (
          <CenteredSpinner label={t('browsePage.loadingAssets')} />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (query.data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={<Upload />} title={t('browsePage.noAssets')} description={t('browsePage.dragOrUpload')} />
        ) : (
          <AssetGrid assets={query.data!.items} />
        )}
      </div>
    </div>
  );
}
